import { useCallback, useEffect, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import type { Event, FullSizeRequest } from '@/types'

export const FULL_SIZE_POLL_MS = 15_000
export const FULL_SIZE_POLL_MAX = 20

export interface FullSizeState {
  url: string | null
  request: FullSizeRequest | null
  stale: boolean
  error: string | null
  asking: boolean
  ask: () => void
}

// Item 398 — the full-size photo of a G100 event: ask for it once, then poll this one event every 15 s (20
// times, 5 min) until the link arrives, the request fails, or the popup closes (cleanup clears the timer).
export function useFullSizePhoto(event: Event | null, queryClient: QueryClient | null): FullSizeState {
  const id = event?.id
  const [url, setUrl] = useState<string | null>(event?.media_details?.full_size_url ?? null)
  const [request, setRequest] = useState<FullSizeRequest | null>(event?.media_details?.full_size_request ?? null)
  const [stale, setStale] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)
  const askingRef = useRef(false)

  // another event: start again from what that event says (during render, so no render shows the old event's state)
  const [prevId, setPrevId] = useState(id)
  if (prevId !== id) {
    setPrevId(id)
    setUrl(event?.media_details?.full_size_url ?? null)
    setRequest(event?.media_details?.full_size_request ?? null)
    setStale(false)
    setError(null)
    setAsking(false)
  }
  // answers of a request started for another event are dropped
  const currentIdRef = useRef(id)
  useEffect(() => {
    currentIdRef.current = id
    askingRef.current = false
  }, [id])

  const refreshRows = useCallback(() => {
    // once: the rows (Recent list, Events page) pick the photo / the request up as well
    queryClient?.invalidateQueries({ queryKey: queryKeys.events.recent() })
    queryClient?.invalidateQueries({ queryKey: ['events', 'paginated'] })
  }, [queryClient])

  // The list row may be older than the server (the Events list does not refetch on its own): when the popup
  // starts for an event that can get a full-size photo and has none yet, ask the server once what is true now.
  const canCheck = !!event?.media_details?.can_request_full_size && !event?.media_details?.full_size_url
  useEffect(() => {
    if (id === undefined || !canCheck) return
    let cancelled = false
    eventsApi.getById(id)
      .then((fresh) => {
        if (cancelled || currentIdRef.current !== id) return
        const media = fresh?.media_details
        if (media?.full_size_url) {
          setUrl(media.full_size_url)
          if (media.full_size_request) setRequest(media.full_size_request)
          refreshRows()
        } else if (media?.full_size_request?.status === 'failed' || media?.full_size_request?.status === 'waiting') {
          setRequest(media.full_size_request)
        }
      })
      .catch(() => { /* silent: keep what the row says */ })
    return () => { cancelled = true }
  }, [id, canCheck, refreshRows])

  const waiting = request?.status === 'waiting' && !url && !stale

  useEffect(() => {
    if (id === undefined || !waiting) return
    let calls = 0
    let cancelled = false
    let timer: number | undefined
    const poll = async () => {
      calls += 1
      let stop = false
      try {
        const fresh = await eventsApi.getById(id)
        if (cancelled) return
        const media = fresh?.media_details
        if (media?.full_size_url) {
          setUrl(media.full_size_url)
          if (media.full_size_request) setRequest(media.full_size_request)
          refreshRows()
          return
        }
        if (media?.full_size_request?.status === 'failed') {
          setRequest(media.full_size_request)
          return
        }
      } catch (err) {
        if (cancelled) return
        // gone (404) or not allowed (401/403): nothing to wait for. Other errors: keep going until the cap.
        const status = (err as { response?: { status?: number } } | null)?.response?.status
        if (status === 404 || status === 401 || status === 403) stop = true
      }
      if (stop) return
      if (calls >= FULL_SIZE_POLL_MAX) { setStale(true); return }
      timer = window.setTimeout(poll, FULL_SIZE_POLL_MS)
    }
    timer = window.setTimeout(poll, FULL_SIZE_POLL_MS)
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer) }
  }, [id, waiting, refreshRows])

  const ask = useCallback(() => {
    if (id === undefined || askingRef.current) return
    askingRef.current = true
    setAsking(true)
    setError(null)
    eventsApi.requestFullSize(id)
      .then((answer) => {
        if (currentIdRef.current !== id) return
        setRequest(answer); setStale(false)
        refreshRows()
      })
      .catch((err) => {
        if (currentIdRef.current !== id) return
        const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
        setError((detail as string) ?? 'network')
      })
      .finally(() => {
        if (currentIdRef.current !== id) return
        askingRef.current = false; setAsking(false)
      })
  }, [id, refreshRows])

  return { url, request, stale, error, asking, ask }
}
